import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  LinearProgress,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DnsIcon from '@mui/icons-material/Dns';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import apiClient from './utils/apiClient';
import PageShell from './PageShell';
import PageContainer from './components/PageContainer';
import PageHeader from './components/PageHeader';
import DataCard from './components/DataCard';
import Footer from './Footer';
import { useVersionStore } from './store/versionStore';
import ScheduledJobsCard from './projects/ScheduledJobsCard';

// Helper function to format timestamp to readable date
const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp);
  return date.toLocaleString();
};

const formatElapsed = (startEpochSeconds) => {
  if (!startEpochSeconds) return null;
  const elapsedMs = Date.now() - startEpochSeconds * 1000;
  if (elapsedMs < 0) return null;
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const formatUptime = (uptime) => {
  if (!uptime) return 'N/A';

  const now = Date.now();
  const uptimeMs = now - uptime;

  const seconds = Math.floor(uptimeMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  return `${days}d ${hours % 24}h ${minutes % 60}m ${seconds % 60}s`;
};

const ServerStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [mfatwinBusy, setMfatwinBusy] = useState(false);
  const [mfatwinError, setMfatwinError] = useState(null);
  const [mfatwinResult, setMfatwinResult] = useState(null);
  const version = useVersionStore(state => state.version);
  const fetchVersion = useVersionStore(state => state.fetchVersion);
  const isLoadingVersion = useVersionStore(state => state.isLoading);
  const versionError = useVersionStore(state => state.error);

  const fetchServerStatus = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/server-status');
      setStatus(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching server status:', err);
      setError('Failed to fetch server status data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleMfatwinUpdate = async () => {
    setMfatwinBusy(true);
    setMfatwinError(null);
    setMfatwinResult(null);
    try {
      const response = await apiClient.post('/server-status-request', { action: 'mfatwin-update' });
      const meta = response?.metadata || {};
      if (meta.ok) {
        setMfatwinResult(meta);
        // Backend refreshes mfatwin_version.py's cache on a successful pull,
        // so re-fetching /v1/version now picks up the new commit/date/message
        // instead of waiting for the next pipeline run or a page reload.
        fetchVersion();
      } else {
        // A well-formed "couldn't pull" outcome (missing dir, not a repo,
        // can't fast-forward, ...) — still a 200 response, distinguished by
        // metadata.ok, not an HTTP error.
        setMfatwinError(meta.error || 'Update failed.');
      }
    } catch (err) {
      // Admin-role denial (403) and an unrecognized action (400) come
      // through here instead, via apiClient's thrown Error.
      console.error('Error updating MFATwin:', err);
      setMfatwinError(err?.message || 'Failed to update MFATwin.');
    } finally {
      setMfatwinBusy(false);
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchServerStatus();

    // Set up auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchServerStatus();
    }, 30000);

    setRefreshInterval(interval);

    // Clean up interval on component unmount
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchVersion();
  }, [fetchVersion]);

  const renderConfigSection = () => {
    if (!status || !status.config) return null;

    return (
      <DataCard noPadding>
        <Accordion defaultExpanded={false} elevation={0} disableGutters sx={{ "&:before": { display: "none" } }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="config-content"
            id="config-header"
            sx={{ px: 3 }}
          >
            <Typography variant="h6">Server Configuration</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, pb: 3 }}>
            <Box>
              {Object.entries(status.config).map(([key, value]) => (
                <Box key={key} sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" component="span" sx={{ fontWeight: 'bold' }}>
                    {key}:
                  </Typography>{' '}
                  <Typography component="span">
                    {typeof value === 'object'
                      ? JSON.stringify(value, null, 2)
                      : String(value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </DataCard>
    );
  };

  return (
    <PageShell>
      <PageContainer>
        <PageHeader
          icon={<DnsIcon />}
          title="Server Status"
          subtitle="Live server health, resources, and configuration"
        />

        {error && <Alert severity="error">{error}</Alert>}

        {loading && !status ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <DataCard title="Server Information">
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                      <Box>
                        <Typography variant="subtitle2">Server ID</Typography>
                        <Typography variant="body1">{status?.id ?? 'N/A'}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box>
                        <Typography variant="subtitle2">Uptime</Typography>
                        <Typography variant="body1">
                          {status?.uptime ? formatUptime(status.uptime) : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box>
                        <Typography variant="subtitle2">Reports Running</Typography>
                        {status?.active_runs?.length > 0 ? (
                          <Accordion elevation={0} disableGutters sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, px: 1.5, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                              <Chip label={status.active_runs.length} color="success" size="small" sx={{ mr: 1 }} />
                              <Typography variant="body2" sx={{ alignSelf: 'center' }}>Active</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1 }}>
                              {status.active_runs.map((run) => (
                                <Box key={run.id} sx={{ py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    <Link to={`/report-list/${run.user_id}/${run.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                      {run.user_fullname || run.user_email || run.user_id}
                                    </Link>
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {run.user_email !== run.user_id && run.user_email ? run.user_email : ''}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {run.product}{run.description ? ` — ${run.description}` : ''}
                                  </Typography>
                                  {run.start_datetime && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Running for {formatElapsed(run.start_datetime)}
                                    </Typography>
                                  )}
                                  <Link to={`/report-list/${run.user_id}/${run.id}`}>
                                    <Typography variant="caption" color="primary">View Report →</Typography>
                                  </Link>
                                </Box>
                              ))}
                            </AccordionDetails>
                          </Accordion>
                        ) : (
                          <Chip label={status?.reports_running ?? 0} color="success" size="small" sx={{ mt: 1 }} />
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box>
                        <Typography variant="subtitle2">Queued Runs</Typography>
                        {status?.queued_runs?.length > 0 ? (
                          <Accordion elevation={0} disableGutters sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, px: 1.5, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                              <Chip label={status.queued_runs.length} color="warning" size="small" sx={{ mr: 1 }} />
                              <Typography variant="body2" sx={{ alignSelf: 'center' }}>Queued</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1 }}>
                              {status.queued_runs.map((run) => (
                                <Box key={run.id} sx={{ py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    <Link to={`/report-list/${run.user_id}/${run.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                      {run.user_fullname || run.user_email || run.user_id}
                                    </Link>
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {run.user_email !== run.user_id && run.user_email ? run.user_email : ''}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {run.product}{run.description ? ` — ${run.description}` : ''}
                                  </Typography>
                                  {run.queued_at && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Waiting for {formatElapsed(run.queued_at)}
                                    </Typography>
                                  )}
                                  <Link to={`/report-list/${run.user_id}/${run.id}`}>
                                    <Typography variant="caption" color="primary">View Report →</Typography>
                                  </Link>
                                </Box>
                              ))}
                            </AccordionDetails>
                          </Accordion>
                        ) : (
                          <Chip label={status?.queued_msgs ?? 0} color="success" size="small" sx={{ mt: 1 }} />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </DataCard>

                <DataCard title="System Resources">
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="subtitle2">CPU Usage</Typography>
                        <LinearProgress
                          variant="determinate"
                          value={status?.cpu_usage ?? 0}
                          color={status?.cpu_usage > 80 ? 'error' : status?.cpu_usage > 60 ? 'warning' : 'success'}
                          sx={{ mt: 1, mb: 1, height: 10, borderRadius: 5 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {status?.cpu_usage !== undefined ? `${Math.round(status.cpu_usage)}%` : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="subtitle2">Memory</Typography>
                        {status?.memory_used !== undefined && status?.memory_available !== undefined ? (
                          <>
                            <LinearProgress
                              variant="determinate"
                              value={(status.memory_used / (status.memory_used + status.memory_available)) * 100}
                              color={(status.memory_used / (status.memory_used + status.memory_available)) > 0.8 ? 'error' :
                                (status.memory_used / (status.memory_used + status.memory_available)) > 0.6 ? 'warning' : 'success'}
                              sx={{ mt: 1, mb: 1, height: 10, borderRadius: 5 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {`${Math.round(status.memory_used)}GB used of ${Math.round(status.memory_used + status.memory_available)}GB total`}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2">N/A</Typography>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="subtitle2">Disk Space</Typography>
                        {status?.disks?.length ? (
                          status.disks.map((d) => {
                            const total = d.total_gb ?? (d.used_gb + d.available_gb);
                            const pct = total > 0 ? (d.used_gb / total) * 100 : 0;
                            return (
                              <Box key={d.path} sx={{ mt: 1.5, '&:first-of-type': { mt: 1 } }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', fontFamily: 'monospace', mb: 0.5 }}
                                  title={d.path}
                                >
                                  {d.path}
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={pct}
                                  color={pct > 80 ? 'error' : pct > 60 ? 'warning' : 'success'}
                                  sx={{ mb: 0.5, height: 10, borderRadius: 5 }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                  {`${Math.round(d.used_gb)}GB used of ${Math.round(total)}GB total`}
                                </Typography>
                              </Box>
                            );
                          })
                        ) : status?.disk_space_used !== undefined && status?.disk_space_available !== undefined ? (
                          // Fallback for a backend/cache without `disks` yet — single "/" reading.
                          <>
                            <LinearProgress
                              variant="determinate"
                              value={(status.disk_space_used / (status.disk_space_used + status.disk_space_available)) * 100}
                              color={(status.disk_space_used / (status.disk_space_used + status.disk_space_available)) > 0.8 ? 'error' :
                                (status.disk_space_used / (status.disk_space_used + status.disk_space_available)) > 0.6 ? 'warning' : 'success'}
                              sx={{ mt: 1, mb: 1, height: 10, borderRadius: 5 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {`${Math.round(status.disk_space_used)}GB used of ${Math.round(status.disk_space_used + status.disk_space_available)}GB total`}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2">N/A</Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </DataCard>

                <DataCard title="System Overview">
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Box>
                        <Typography variant="subtitle2">Started At</Typography>
                        <Typography>{status?.uptime ? formatDate(status.uptime) : 'N/A'}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Box>
                        <Typography variant="subtitle2">Version</Typography>
                        <Typography>{version?.version ?? 'Loading...'}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Box>
                        <Typography variant="subtitle2">Commit</Typography>
                        <Typography>
                          {version?.commit ? (
                            <a
                              href={`https://github.com/Metalyticsbio/continuous_mfa/commit/${version.commit}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {version.commit.slice(0, 7)}
                            </a>
                          ) : (
                            'N/A'
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Box>
                        <Typography variant="subtitle2">Commit Date</Typography>
                        <Typography>{version?.commit_date ? formatDate(version.commit_date) : 'N/A'}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Box>
                        <Typography variant="subtitle2">Comment</Typography>
                        <Typography>{version?.commit_message ?? 'N/A'}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </DataCard>

                <DataCard
                  title="MFATwin Engine"
                  action={
                    <Button
                      variant="outlined"
                      onClick={handleMfatwinUpdate}
                      disabled={mfatwinBusy}
                      startIcon={mfatwinBusy ? <CircularProgress size={18} /> : <UpgradeIcon />}
                    >
                      {mfatwinBusy ? 'Updating...' : 'Upgrade'}
                    </Button>
                  }
                >
                  {mfatwinError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMfatwinError(null)}>
                      {mfatwinError}
                    </Alert>
                  )}
                  {mfatwinResult && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMfatwinResult(null)}>
                      {mfatwinResult.updated
                        ? `Updated to commit ${mfatwinResult.commit?.slice(0, 7)}.`
                        : 'Already up to date.'}
                    </Alert>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="subtitle2">Commit</Typography>
                        <Typography>
                          {version?.mfatwin?.commit ? (
                            <a
                              href={`https://github.com/Metalyticsbio/mfatwin/commit/${version.mfatwin.commit}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {version.mfatwin.commit.slice(0, 7)}
                            </a>
                          ) : (
                            'N/A'
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="subtitle2">Commit Date</Typography>
                        <Typography>{version?.mfatwin?.commit_date ? formatDate(version.mfatwin.commit_date) : 'N/A'}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Box>
                        <Typography variant="subtitle2">Comment</Typography>
                        <Typography>{version?.mfatwin?.commit_message ?? 'N/A'}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </DataCard>

                <ScheduledJobsCard />

                {renderConfigSection()}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                    Last updated: {new Date().toLocaleString()}
                  </Typography>
                </Box>
              </>
            )}
      </PageContainer>
    </PageShell>
  );
};

export default ServerStatus;
